import { Directive, ElementRef, HostListener, Input, OnDestroy, Renderer2, inject } from '@angular/core';

import { CustomActiveStateRegistry } from './custom-active-state-registry.service';

/**
 * A directive that allows adding a custom active state class to a component.
 * This allows for custom handling of :active selector in cases where :active is not reliable (e.g. IOS devices).
 * The active state applies the custom class "isCustomActiveState" by default but this can be customized.
 *
 * Usage:
 * Add the `msCustomActiveState` attribute to an element.
 *
 * Inputs:
 * - `activeClass`: Defines a custom class to be applied to the target element during active state. (default: `isCustomActiveState`).
 */
@Directive({
    selector: '[msCustomActiveState]',
})
export class CustomActiveStateDirective implements OnDestroy {
    @Input() activeClass = 'isCustomActiveState';

    private readonly element: ElementRef = inject(ElementRef);
    private readonly registry = inject(CustomActiveStateRegistry);
    private readonly renderer2 = inject(Renderer2);

    private _isActive = false;

    @HostListener('mousedown', ['$event'])
    @HostListener('touchstart', ['$event'])
    triggerInteractionStart(): void {
        if (this._isActive) {
            return;
        }

        this.renderer2.addClass(this.element.nativeElement, this.activeClass);
        this.registry.register(this);
        this._isActive = true;
    }

    triggerInteractionEnd(): void {
        if (!this._isActive) {
            return;
        }

        this.renderer2.removeClass(this.element.nativeElement, this.activeClass);
        this._isActive = false;
    }

    ngOnDestroy(): void {
        this.registry.unregister(this);
    }
}
